import React, { useState, useEffect } from "react";
import { Modal, Form, Input, InputNumber, Button, Row, Col, Typography, Radio, Alert } from "antd";
import { SPRAY_PAUSE_REASON } from '@iso/constants/sprayJobStates';

const { Text } = Typography;

/**
 * Captures the zone where the operator paused or ran out of mix.
 * Used by Stop and Refill actions.
 *
 * Opens with all fields blank — the operator types where they
 * actually paused. The previous behaviour pre-filled from the first
 * zone's start coords (so a pause mid-Zone-3 would suggest Zone 1's
 * starting bench / row, which was always wrong and the operator had
 * to clear anyway).
 *
 * Pause mode also offers a "Reason" choice:
 *   - Pause (default)             — operator just stopping, will resume
 *   - Unplanned refill            — out of mix mid-zone; reveals a
 *                                   "Total volume to load" input. On
 *                                   submit the backend pauses the job
 *                                   AND injects a refill marker
 *                                   BEFORE the active zone so the
 *                                   existing planned-refill workflow
 *                                   (Mix Recipe modal) gates the
 *                                   resume.
 *
 * onSubmit payload shape:
 *   { letter, bench, row, notes, reason, liters }
 *
 *   reason is one of 'pause' | 'unplanned_refill'; liters is required
 *   (and > 0) when reason === 'unplanned_refill', otherwise null.
 *
 * @param {boolean} visible
 * @param {Function} onCancel
 * @param {Function} onSubmit
 * @param {string} mode  'pause' | 'refill' — affects the modal copy/CTA
 * @param {Object} job   The current spray job
 * @param {boolean} loading
 */
// eslint-disable-next-line no-unused-vars
const PauseZoneModal = ({ visible, onCancel, onSubmit, mode = 'pause', job, loading = false }) => {
  const [letter, setLetter] = useState('');
  const [bench, setBench] = useState(null);
  const [row, setRow] = useState(null);
  const [notes, setNotes] = useState('');
  const [letterError, setLetterError] = useState(null);
  // Pause mode reason — 'pause' (default) | 'unplanned_refill'.
  // Ignored when mode === 'refill' (existing planned-refill flow has
  // its own dedicated modal in SprayJobActionsPanel).
  const [reason, setReason] = useState(SPRAY_PAUSE_REASON.PAUSE);
  const [liters, setLiters] = useState(null);
  const [litersError, setLitersError] = useState(null);

  // Reset every field to blank each time the modal opens so the
  // operator types where they actually paused. job is no longer read
  // by the effect but the prop stays in the API for future use —
  // e.g. validating the entered position against the job's planned
  // zones.
  useEffect(() => {
    if (!visible) return;
    setLetter('');
    setBench(null);
    setRow(null);
    setNotes('');
    setLetterError(null);
    setReason(SPRAY_PAUSE_REASON.PAUSE);
    setLiters(null);
    setLitersError(null);
  }, [visible]);

  const isRefill = mode === 'refill';
  const isUnplanned = !isRefill && reason === SPRAY_PAUSE_REASON.UNPLANNED_REFILL;
  const title = isRefill
    ? 'Refill required — where did you finish?'
    : 'Where are you pausing?';
  const ctaText = isRefill
    ? 'Create refill job'
    : (isUnplanned ? 'Log refill & pause' : 'Stop');
  const description = isRefill
    ? 'This tank is done. Tell us where you finished so the next refill spray job starts from the right point. The new job inherits the same tractor and water tank size.'
    : 'Please tell us where you paused. This will be logged in the spray history.';

  const handleLetterChange = (e) => {
    setLetter(e.target.value);
    if (letterError) setLetterError(null);
  };

  const handleLitersChange = (v) => {
    setLiters(v);
    if (litersError) setLitersError(null);
  };

  const handleSubmit = () => {
    let blocked = false;
    if (!letter || !letter.trim()) {
      setLetterError('Field is required');
      blocked = true;
    }
    if (isUnplanned) {
      const n = Number(liters);
      if (!Number.isFinite(n) || n <= 0) {
        setLitersError('Enter the total volume you are about to load');
        blocked = true;
      }
    }
    if (blocked) return;
    onSubmit({
      letter: letter.trim(),
      bench: bench || null,
      row: row || null,
      notes: notes?.trim() || null,
      reason: isRefill ? 'refill' : reason,
      liters: isUnplanned ? Number(liters) : null,
    });
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>Cancel</Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading} className="btn-success">
          {ctaText}
        </Button>,
      ]}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {description}
      </Text>

      {/* Reason picker is pause-mode-only. Refill mode already lives
          in the planned-refill flow with its own dedicated modal. */}
      {!isRefill && (
        <Form layout="vertical" style={{ marginBottom: 8 }}>
          <Form.Item label="Reason">
            <Radio.Group
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={SPRAY_PAUSE_REASON.PAUSE}>Pause</Radio.Button>
              <Radio.Button value={SPRAY_PAUSE_REASON.UNPLANNED_REFILL}>Unplanned refill</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      )}

      {isUnplanned && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Unplanned refill"
          description={
            'A new refill task will appear in the Actions list — complete the '
            + 'Mix Recipe + Spray PPE check before you can resume the zone.'
          }
        />
      )}

      <Form layout="vertical">
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Form.Item
              label="Field"
              required
              validateStatus={letterError ? 'error' : ''}
              help={letterError || ''}
            >
              <Input
                value={letter}
                onChange={handleLetterChange}
                placeholder="e.g. A, K6, PD"
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Bench">
              <InputNumber
                value={bench}
                onChange={setBench}
                min={1}
                max={100}
                placeholder="1-100"
                inputMode="numeric"
                style={{ width: '100%' }}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Row">
              <InputNumber
                value={row}
                onChange={setRow}
                min={1}
                max={999}
                placeholder="1-999"
                inputMode="numeric"
                style={{ width: '100%' }}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {isUnplanned && (
          <Form.Item
            label="Total volume to load (L)"
            required
            validateStatus={litersError ? 'error' : ''}
            help={litersError || 'Litres about to be poured into the tank for this top-up.'}
          >
            <InputNumber
              value={liters}
              onChange={handleLitersChange}
              min={1}
              step={1}
              placeholder="e.g. 150"
              inputMode="numeric"
              style={{ width: '100%' }}
              size="large"
            />
          </Form.Item>
        )}

        <Form.Item label="Notes (optional)">
          <Input.TextArea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="e.g. nozzle blocked, switched to next bench"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PauseZoneModal;
